/**
 * Reports this app's height to the page embedding it.
 *
 * Without this, the Weebly Embed Code element renders a fixed-height iframe
 * with its own scrollbar inside the club page, which is unusable on a phone
 * once the reservation form grows past the box.
 *
 * The matching listener lives in `embed.html`, which is what gets pasted into
 * Weebly. Keep the message shape in sync between the two.
 */

const MESSAGE_TYPE = 'lmac:embed-height';
const SCROLL_TYPE = 'lmac:embed-scroll';

export interface EmbedHeightMessage {
  type: typeof MESSAGE_TYPE;
  height: number;
}

/**
 * Starts reporting height on every layout change. Returns a teardown function.
 *
 * Safe to call when not embedded — `window.parent === window` at the top level,
 * in which case this does nothing.
 */
export function reportHeightToParent(
  element: HTMLElement = document.documentElement,
): () => void {
  if (window.parent === window) return () => {};

  let lastSent = -1;

  const send = (): void => {
    // offsetHeight rather than scrollHeight: scrollHeight can only grow while
    // the parent iframe is already tall, so the frame would never shrink back
    // after a tall step of the form is replaced by a short confirmation.
    const height = Math.ceil(element.offsetHeight);
    if (height === lastSent || height === 0) return;
    lastSent = height;
    const message: EmbedHeightMessage = { type: MESSAGE_TYPE, height };
    // '*' is acceptable here because the payload is a single integer with no
    // secrets in it. Never widen this message to carry vendor details.
    window.parent.postMessage(message, '*');
  };

  const observer = new ResizeObserver(send);
  observer.observe(element);

  // Fonts and images landing late both change the height after first paint.
  window.addEventListener('load', send);
  document.fonts?.ready.then(send).catch(() => {});
  send();

  return () => {
    observer.disconnect();
    window.removeEventListener('load', send);
  };
}

/**
 * Asks the embedding page to scroll the iframe into view.
 *
 * The embed can sit a long way down the club's page, below the flyer. When a
 * vendor moves from the availability preview into the reservation flow the
 * frame grows considerably, and without this they are left looking at whatever
 * was above it, with the step they just asked for somewhere off screen.
 *
 * A no-op when not embedded, and ignored by any parent that has not opted in.
 */
export function requestParentScroll(): void {
  if (window.parent === window) return;
  window.parent.postMessage({ type: SCROLL_TYPE }, '*');
}
